import Header from '../Header2';
import "../../styles/Project.css"
import Vector from "../../assets/svg/editIcon.svg";
import Delete from "../../assets/svg/DeleteIcon.svg";
import { useState, useRef, useEffect } from "react";
import DatePicker from "../../Component/DatePicker";
import ReactPaginate from 'react-paginate';
import axiosInstance from '../../axiosInstance';
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';
import CommonFooter from '../../CommonFooter/CommonFooter';
import { useRolePermission } from '../../hooks/useRolePermission';

const Financial = () => {

  const checkPermission = useRolePermission();

  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortBy, setSortBy] = useState("dateCreated");
  const [reload, setReload] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchFinancialAssumptions = async (term = '') => {
    try {
      const response = await axiosInstance.get(`/costsolution/financial`, {
        params: { limit, skip, sortBy, search: term || undefined }
      });

      const data = response.data;

      if (Array.isArray(data.financialassumptions)) {
        setTableData(data.financialassumptions);
        setTotal(data.totalCount || 0);
        setTotalPages(Math.ceil((data.totalCount || 0) / limit));
      } else {
        setTableData([]);
      }
    } catch (error) {
      setError(error.message);
      console.error("Error fetching financial assumptions:", error);
      toast.error("Failed to fetch financial assumptions");
    }
  };

  useEffect(() => {
    fetchFinancialAssumptions(searchTerm);
  }, [skip, limit, reload, sortBy, searchTerm]);


  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    setSkip(selected * limit);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(0);
    setSkip(0);
  };

  const handleDateSelection = (date) => {
    console.log("Selected Date:", date);
  };

  const handleAddNewClick = () => {
    navigate("/financialCreation");
  };

  const isAllSelected = selectedRows.length === tableData.length;
  const isAnySelected = selectedRows.length > 0;

  const handleSelectAll = () => {
    setSelectedRows(isAllSelected ? [] : tableData.map((row) => row.id));
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDeleteRow = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await axiosInstance.delete(
        `/costsolution/financial/delete/${deleteId}`
      );

      if (response.status === 200) {
        toast.success("Financial assumption deleted successfully!");
        setTableData((prevData) => prevData.filter((row) => row.id !== deleteId));
        setTotal((prevTotal) => prevTotal - 1);
        setReload(prev => !prev);
      } else {
        toast.error(`Failed to delete the financial assumption!`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong while deleting!");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handleEditClick = async (id) => {
    try {
      // First get the financial assumption details
      const financialRes = await axiosInstance.get(`/costsolution/financial/get/${id}`);
      const financialData = financialRes.data.projectfinancialassumptions;

      // Get project details
      const projectRes = await axiosInstance.get(`/costsolution/costlines/getprojects/${financialData.projectId}`);

      // Get all related data in parallel
      const [
        categoriesRes,
        periodsRes,
        structureRes,
        escalationRes,
        costCategoriesRes
      ] = await Promise.all([
        axiosInstance.get(`/costsolution/costlines/getcategory/${financialData.projectId}`),
        axiosInstance.get(`/costsolution/costlines/getperiods/${financialData.projectId}`),
        axiosInstance.get(`/costsolution/costlinestructures/${id}`),
        axiosInstance.get(`/costsolution/financial/costescalation/${id}`), // Note: using id not projectId
        axiosInstance.get(`/costsolution/financial/costcategories/${id}`) // Note: using id not projectId
      ]);

      // Format the data for the edit form
      const combinedData = {
        ...financialData,
        project: projectRes.data.projects, // Changed from project to projects to match API
        categories: categoriesRes.data.projectcategory || [],
        periods: periodsRes.data.ProjectPeriod || [],
        costlinestructure: structureRes.data.costlinestructure || [],
        costEscalations: escalationRes.data.costescalation || [],
        costCategories: costCategoriesRes.data.costcategories || [] // Match API response structure
      };

      navigate(`/financialCreation`, {
        state: {
          financialData: combinedData,
          isEditMode: true,
          financialAssumptionId: id
        }
      });

    } catch (error) {
      console.error("Error fetching financial assumption data:", error);
      toast.error("Failed to load financial assumption data");
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    setSkip(0);
    await fetchFinancialAssumptions(term);
  };

  return (
    <>
      <Header onSearch={handleSearch} />
      <div className="container">
        <div className="header">
          <div className="left-header">
            <h3>All Financial Assumptions ({total})</h3>
          </div>
          <div className="right-header">
            {/* {!isAnySelected ? (
              <>
                <DatePicker onDateChange={handleDateSelection} />
                <h4>Sort by :</h4>
                <div className="dropdown">
                  <select 
                    id="options" 
                    className="dropdown-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="dateCreated">Date Created</option>
                    <option value="project">Project</option>
                  </select>
                </div>
              </>
            ) : (
              <button 
                id="deleteBtn" 
                className="deleteBtn" 
                onClick={() => handleDeleteRow(selectedRows[0])}
              >
                Delete
              </button>
            )} */}

            {checkPermission("Financial Assumption", "create_permission") && !isAnySelected && (
              <button id="addNewBtn" onClick={handleAddNewClick}>
                Add New
              </button>
            )}

          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: "1px" }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>S.No</th>
                <th>Project Name</th>
                <th>Modified Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                    />
                  </td>
                  <td>{skip + index + 1}</td>
                  <td>{row.projectName}</td>
                  <td>{row.modifieddate}</td>
                  <td>
                    <div className="actions">

                      {checkPermission("Financial Assumption", "edit_permission") && (
                        <span className="edit-icon">
                          <img
                            src={Vector}
                            alt="Edit Icon"
                            width="20"
                            height="20"
                            onClick={() => handleEditClick(row.id)}
                            style={{ cursor: "pointer" }}
                          />
                        </span>
                      )}

                      {checkPermission("Financial Assumption", "delete_permission") && (
                        <span
                          className="delete-icon"
                          onClick={() => handleDeleteRow(row.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <img src={Delete} alt="Delete Icon" width="40" height="40" />
                        </span>
                      )}

                    </div>
                  </td>
                    </tr>
                  ))   ) : (
                <tr>
                  {searchTerm && tableData.length === 0 && (
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', fontSize: '14px', color: '#666' }}>
                      No Financial found matching "{searchTerm}"
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="pagination-container d-flex align-items-center justify-content-between mt-3">
          <div className="Limit-Dropdown">
            <label htmlFor="limit" className="fw-semibold">Items per page:</label>
            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="form-select d-inline w-auto"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </select>
          </div>

          <div className="d-flex align-items-center gap-4">
            <ReactPaginate
              previousLabel={<span style={{ userSelect: "none" }}>Previous</span>}
              nextLabel={<span style={{ userSelect: "none" }}>Next</span>}
              pageCount={totalPages > 0 ? totalPages : 1}
              onPageChange={handlePageClick}
              containerClassName={"pagination d-flex"}
              activeClassName={"active"}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              forcePage={currentPage}
            />
          </div>
        </div>
      </div>
      <CommonFooter />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this financial assumptions?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default Financial;